import config from './config';

type ImageSize = 'xs' | 'sm' | 'md' | 'lg';

const PUBLIC_DOCS_IMAGE_ORIGINS = [
    'https://assets.kalshi.com',
    'https://kalshi-public-docs.s3.amazonaws.com',
    'https://kalshi-public-docs.s3.us-east-1.amazonaws.com',
];

/** Same-origin path that proxies to the images CloudFront origin via a
 * Next.js rewrite (see `next.config.js`). Browser-bound image URLs are
 * rewritten to live under this prefix so they pick up the
 * `max-age=3600, stale-while-revalidate=86400` `Cache-Control` headers we
 * attach to it — repeats serve from cache instantly (no reload blink) and
 * artwork updates at the same ticker path propagate within a day. */
const CDN_IMAGES_PROXY_PREFIX = '/cdn-images';

/** Same-origin proxy prefix for the public images S3 bucket. */
const CDN_PUBLIC_IMAGES_PROXY_PREFIX = '/cdn-public-images';

/**
 * Rewrites an absolute images-CloudFront URL to the same-origin
 * `/cdn-images/...` proxy path. No-op for any URL that isn't pointed at the
 * configured CloudFront origin (e.g. raw S3 paths, local assets, fully
 * relative URLs already pointing at the proxy).
 *
 * Use this only for URLs that will be fetched by the browser — server-side
 * URL generation (OG images, share cards, route handlers that serve image
 * responses) should keep using the absolute CloudFront URL since `fetch()`
 * in those contexts cannot resolve a same-origin path.
 */
export const proxyCdnImageUrl = (url: string): string => {
    if (!url) return url;
    const cdnOrigin = config.kalshiImagesCloudfrontUrl;
    if (!url.startsWith(cdnOrigin)) return url;
    return `${CDN_IMAGES_PROXY_PREFIX}${url.slice(cdnOrigin.length)}`;
};

/** Rewrites an absolute public-images-S3 URL to the same-origin `/cdn-public-images/...` proxy path. No-op for any other origin. */
const proxyPublicImageUrl = (url: string): string => {
    if (!url) return url;
    const origin = config.kalshiPublicImagesS3BucketUrl;
    if (!url.startsWith(origin)) return url;
    return `${CDN_PUBLIC_IMAGES_PROXY_PREFIX}${url.slice(origin.length)}`;
};

/**
 * Converts an S3 image URL to its CDN equivalent.
 * Replaces the S3 origin with the CloudFront CDN URL and appends a `?size` param when provided.
 * Safe to call on non-S3 URLs — they are returned unchanged.
 */
export const toCdnImageUrl = (url: string, size?: ImageSize): string => {
    const publicDocsOrigin = PUBLIC_DOCS_IMAGE_ORIGINS.find((origin) => url === origin || url.startsWith(`${origin}/`));
    const cdnUrl = publicDocsOrigin ? url.replace(publicDocsOrigin, config.kalshiImagesCloudfrontUrl) : url;
    if (!size) return cdnUrl;
    // Skip size param for .avif images — the CDN resizer doesn't support AVIF and returns 502
    if (cdnUrl.includes('.avif')) return cdnUrl;
    const separator = cdnUrl.includes('?') ? '&' : '?';
    return `${cdnUrl}${separator}size=${size}`;
};

/**
 * Normalizes an image URL into a stable key for equality comparison: routes through
 * the CDN and strips any query string so cache-busting / size params don't produce
 * false negatives when comparing two visually identical images.
 */
export const normalizeImageKey = (url: string | null | undefined): string | null => {
    if (!url) return null;
    const cdn = toCdnImageUrl(url);
    const qIdx = cdn.indexOf('?');
    return qIdx === -1 ? cdn : cdn.slice(0, qIdx);
};

/**
 * Returns true when every item in `items` resolves (via `getItemImageUrl`) to the
 * same image URL as `headerImageUrl`. Used to detect that the "header" image and
 * all per-item images are duplicates and at least one of them can be suppressed.
 *
 * Guards:
 *   - Empty items -> false
 *   - Missing/empty header image -> false
 *   - Any item with no resolvable image -> false (don't hide placeholders that
 *     aren't actually duplicates)
 */
export const allImagesMatchHeader = <T>({
    items,
    headerImageUrl,
    getItemImageUrl,
}: {
    items: readonly T[];
    headerImageUrl: string | null | undefined;
    getItemImageUrl: (item: T) => string | null | undefined;
}): boolean => {
    if (items.length === 0) return false;
    const headerKey = normalizeImageKey(headerImageUrl);
    if (!headerKey) return false;
    return items.every((item) => {
        const itemKey = normalizeImageKey(getItemImageUrl(item));
        return !!itemKey && itemKey === headerKey;
    });
};

const imageQueryParams = (imageSize?: ImageSize): string => (!!imageSize ? `?size=${imageSize}` : '');

// TODO: Public images should be moved to the public docs bucket ASAP,
// as that's where we've elected to store series images and have enabled dynamic
// sizing. For now dynamic sizing *will not* work in this bucket.
/**
 * Resolves a public-images-S3 asset URL.
 *
 * Pass `{ absolute: true }` when the URL will be consumed outside the main
 * app's Next.js host (e.g. the widget-app, which has no `/cdn-public-images`
 * rewrite), or in any CSS `url()` / inline style that runs in such a context.
 * Browser callers inside the main app should use the default (proxied) form.
 */
export const getPublicImage = (
    imageName: string,
    imageSize?: ImageSize,
    { absolute = false }: { absolute?: boolean } = {},
): string => {
    const absoluteUrl = `${config.kalshiPublicImagesS3BucketUrl}/${imageName}`;
    return (absolute ? absoluteUrl : proxyPublicImageUrl(absoluteUrl)) + imageQueryParams(imageSize);
};

/**
 * Resolves the display image URL for an event or card.
 * Prefers product_metadata.custom_image_url when set (backend-controlled override),
 * falls back to the dynamically constructed series image URL.
 */
export const getEventImageUrl = ({
    seriesTicker,
    customImageUrl,
}: {
    seriesTicker?: string;
    customImageUrl?: string;
}): string => {
    if (customImageUrl) return customImageUrl;
    if (seriesTicker) return getSeriesImageSrc(seriesTicker);
    return '';
};

export const getSeriesImageSrc = (seriesTicker: string, imageSize: ImageSize = 'sm'): string => {
    let newSeriesTicker = seriesTicker;
    if (seriesTicker === 'SENATELA') {
        newSeriesTicker = 'SENATEKY';
    }
    return (
        config.kalshiImagesCloudfrontUrl +
        '/series-images-webp/' +
        newSeriesTicker +
        '.webp' +
        imageQueryParams(imageSize)
    );
};

export const getNextImageUrl = (imgUrl: string, width = 1920, quality = 80): string => {
    const baseURL = window.location.protocol + '//' + window.location.host;
    return `${baseURL}/_next/image?url=${imgUrl}&w=${width}&q=${quality}`;
};

export const compressImage = (base64String: string, targetWidth: number, targetHeight: number, _quality: number) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) {
                reject(new Error('Unable to obtain 2D canvas context'));
                return;
            }

            const ratio = Math.min(targetWidth / img.width, targetHeight / img.height);
            const scaledWidth = Math.max(1, Math.round(img.width * ratio));
            const scaledHeight = Math.max(1, Math.round(img.height * ratio));

            canvas.width = scaledWidth;
            canvas.height = scaledHeight;
            ctx.imageSmoothingQuality = 'high';

            ctx.clearRect(0, 0, scaledWidth, scaledHeight);
            ctx.drawImage(img, 0, 0, scaledWidth, scaledHeight);

            canvas.toBlob((blob) => {
                if (!blob) {
                    reject(new Error('Failed to convert canvas to blob'));
                    return;
                }

                const reader = new FileReader();
                reader.onloadend = () => {
                    resolve(reader.result);
                };
                reader.readAsDataURL(blob);
            }, 'image/png');
        };
        // The error Event carries no failure detail and is not an Error instance, so
        // rejecting with it (or with nothing) yields stackless RUM noise — reject
        // with a real Error instead.
        img.onerror = () => reject(new Error('Failed to load image for compression'));
        img.src = base64String;
    });
};

/**
 * Resolves the avatar URL for a Social profile image.
 *
 * Returns the absolute social-assets URL for browser and server callers. Keeping
 * profile images on the asset origin avoids sending `kalshi.com` cookies with
 * public image requests.
 */
export const getIdeasProfileImageUrl = (profileImagePath: string): string => {
    const parsedImagePath = profileImagePath || 'egg';
    return `${config.kalshiSocialPublicS3BucketUrl}/profile-pictures/${parsedImagePath}.webp`;
};

export const hasRealProfileImage = (profileImagePath: string | null | undefined): profileImagePath is string =>
    !!profileImagePath && profileImagePath.toLocaleLowerCase() !== 'egg';
